import type { ReactNode } from "react";
import { forwardRef } from "react";
import { AlertCircle, Check, CheckCheck, Loader2 } from "lucide-react";

import type { ChatDeliveryStatus, ChatReadReceipt } from "@/types/task-thread";
import { accentVars } from "@/lib/theme/accent-utils";
import { cn } from "@/lib/utils";
import { ChatMessageAvatar } from "./chat-message-avatar";

export type ChatMessageBubbleTone = "operator" | "system" | "external";

export type ChatMessageBubbleProps = {
  tone?: ChatMessageBubbleTone;
  meta?: string;
  isStacked?: boolean;
  className?: string;
  children?: ReactNode;
  deliveryStatus?: ChatDeliveryStatus;
  deliveryTimestamp?: string;
  readReceipts?: ChatReadReceipt[];
};

const toneClassMap: Record<ChatMessageBubbleTone, { container: string; meta: string }> = {
  operator: {
    container:
      "bg-[var(--token-foreground-default)] text-[var(--token-foreground-primary)] shadow-[var(--shadow-sm)]",
    meta: "text-[var(--token-foreground-primary)]/70",
  },
  system: {
    container:
      "bg-[var(--token-foundation-surface-subtle)] text-[var(--token-foreground-default)]",
    meta: "text-muted-foreground",
  },
  external: {
    container:
      "bg-[color-mix(in_oklab,var(--token-surface-default)_90%,transparent)] text-foreground",
    meta: "text-muted-foreground/75",
  },
};

const toneStatusClassMap: Record<ChatMessageBubbleTone, string> = {
  operator: "text-[var(--token-foreground-primary)]/80",
  system: "text-muted-foreground",
  external: "text-muted-foreground",
};

const toneFailureColorMap: Record<ChatMessageBubbleTone, string> = {
  // The operator bubble is the cream `foreground-default` fill, so it needs
  // the dark `strong` red; the near-white `foreground` variants vanish on it.
  operator: accentVars.danger.strong,
  system: accentVars.danger.core,
  external: accentVars.danger.core,
};

const deliveryStatusCopy: Record<ChatDeliveryStatus, { label: string; icon: typeof Loader2 }> = {
  sending: { label: "Sending", icon: Loader2 },
  sent: { label: "Sent", icon: Check },
  delivered: { label: "Delivered", icon: CheckCheck },
  read: { label: "Read", icon: CheckCheck },
  failed: { label: "Delivery failed", icon: AlertCircle },
};

export const ChatMessageBubble = forwardRef<HTMLDivElement, ChatMessageBubbleProps>(function ChatMessageBubble(
  { tone = "operator", meta, isStacked, className, children, deliveryStatus, deliveryTimestamp, readReceipts },
  ref,
) {
  const toneClasses = toneClassMap[tone];
  const statusMeta = deliveryStatus ? deliveryStatusCopy[deliveryStatus] : null;
  const hasReceipts = Boolean(readReceipts?.length);
  const statusClassName = cn(
    "mt-3 flex flex-wrap items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.24em]",
    deliveryStatus === "failed" ? undefined : toneStatusClassMap[tone],
  );
  const statusStyle = deliveryStatus === "failed" ? { color: toneFailureColorMap[tone] } : undefined;

  return (
    <article
      ref={ref}
      className={cn(
        "max-w-full min-w-0 break-words rounded-[var(--radius-xxl)] px-4 py-4 transition-shadow",
        toneClasses.container,
        isStacked && "mt-[var(--space-3xs)] opacity-95",
        className,
      )}
    >
      {meta ? (
        <p className={cn("text-[11px] font-semibold uppercase tracking-[0.32em]", toneClasses.meta)}>
          {meta}
        </p>
      ) : null}
      {children ? <div className={cn(meta ? "mt-3" : undefined, "space-y-3")}>{children}</div> : null}
      {statusMeta ? (
        <div className={statusClassName} style={statusStyle}>
          <statusMeta.icon
            className={cn(
              "h-3.5 w-3.5",
              deliveryStatus === "sending" && "animate-spin",
            )}
            aria-hidden
          />
          <span>
            {statusMeta.label}
            {deliveryTimestamp ? ` • ${deliveryTimestamp}` : null}
          </span>
        </div>
      ) : null}
      {hasReceipts ? <ReadReceiptsRow receipts={readReceipts!} tone={tone} /> : null}
    </article>
  );
});

type ReadReceiptsRowProps = {
  receipts: ChatReadReceipt[];
  tone: ChatMessageBubbleTone;
};

function ReadReceiptsRow({ receipts, tone }: ReadReceiptsRowProps) {
  if (!receipts.length) {
    return null;
  }

  const label = formatReadReceiptSummary(receipts);

  return (
    <div
      className={cn(
        "mt-2 flex flex-wrap items-center gap-3 text-[11px] font-medium",
        toneStatusClassMap[tone],
      )}
    >
      <span className="uppercase tracking-[0.24em] text-muted-foreground/70">Read</span>
      <div className="flex -space-x-2">
        {receipts.slice(0, 3).map((receipt) => (
          <ChatMessageAvatar
            key={receipt.id}
            initials={receipt.avatarInitials}
            variant={receipt.role}
            size="sm"
            className="h-7 w-7 border-2 border-background text-[10px]"
            alt={receipt.name}
          />
        ))}
      </div>
      <span className="text-[11px] tracking-[0.08em] uppercase">{label}</span>
    </div>
  );
}

function formatReadReceiptSummary(receipts: ChatReadReceipt[]) {
  if (receipts.length === 1) {
    const [first] = receipts;
    return `${first.name} · ${first.timestamp}`;
  }

  if (receipts.length === 2) {
    return `${receipts[0].name} and ${receipts[1].name} · ${receipts[1].timestamp}`;
  }

  const last = receipts[receipts.length - 1];
  return `${receipts[0].name} and ${receipts.length - 1} others · ${last?.timestamp ?? ""}`;
}
